import React, { useContext, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Trash2, RotateCcw, Columns3, FileText, Map as MapIcon, ShoppingCart, CalendarCheck } from 'lucide-react';
import Swal from 'sweetalert2';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import apiClient from '../../services/api/apiClient.js';
import { AuthContext } from '../../contexts/AuthContext.jsx';
import useListado from '../../hooks/useListado.js';
import DataTable from '../../components/common/DataTable.jsx';
import Pagination from '../../components/common/Pagination.jsx';
import { exportTablePdf, exportTableExcel, buildTablePdf, agregarLogosPdf } from '../../utils/exportUtils.js';
import EmailModal from '../../components/common/EmailModal.jsx';
import { enviarCorreoAdjunto, plantillaCorreoV1 } from '../../services/api/mail.js';
import EtaMasivaModal from './EtaMasivaModal.jsx';
import ArticulosContenedorModal from './ArticulosContenedorModal.jsx';
import StatusContenedorModal, { STATUS_OPCIONES } from './StatusContenedorModal.jsx';
import MapaTrackingModal from '../../components/contenedores/MapaTrackingModal.jsx';
import { traerCatalogo, ordenarPor as ordenarCatalogo, PUERTOS, MODALIDADES, ASEGURADO_OPCIONES } from './catalogos.js';

/**
 * Listado de Contenedores — el objeto central del negocio de Ibu (~50 campos, ver
 * `contenedores.model.js` en IbuBack2). No usa `ResourcePage` genérico: tiene más
 * columnas/acciones y el alta/edición vive en una página completa aparte
 * (`ContenedorFormPage.jsx`, ver `App.jsx`) en vez de un modal.
 *
 * El servicio de backend (`contenedores.service.js#construirFiltro`) filtra por
 * `status`/`is_active` y, desde el fix de la auditoría 2026-09-28 (ver DUDAS.md
 * D-11.1), también soporta `search` libre sobre los campos de texto locales
 * (numeroInterno/contenedor/status/puerto/modalidad) — igual que V1
 * (`ListadoContenedores.jsx#commentsData`), salvo por las columnas de nombres
 * poblados (cliente/naviera/agente/forwarder), que V1 sí buscaba pero que aquí
 * quedan fuera de alcance porque requerirían una agregación con `$lookup`.
 *
 * Columnas Cliente/Naviera/Agente Aduanal/Forwarder/Patente: el backend YA popula
 * estas refs (`contenedores.service.js#POPULATE_COMPLETO`) y `nombreDe()` ya sabe
 * leerlas -- si en el navegador aparecen vacías en filas reales que SÍ tienen esos
 * campos asignados (confirmado contra `ibuPruebas` en la auditoría 2026-09-28, ver
 * DUDAS.md), NO es un bug de este archivo: es que el proceso de `IbuBack2` que sigue
 * corriendo es de ANTES de que `POPULATE_COMPLETO` se agregara a
 * `contenedores.service.js` y necesita reiniciarse para tomar el cambio.
 */
const nombreDe = (arr, campo = 'name') => (Array.isArray(arr) && arr[0] ? arr[0][campo] : '—');

// Preferencia de columnas visibles -- pedido explícito del usuario (2026-09-29):
// con las 15 columnas reales de V1, un botón para mostrar/ocultar columnas evita el
// scroll horizontal sin tener que sacrificar ninguna columna de forma permanente.
// No.Interno/Contenedor/Acciones son obligatorias (identifican la fila); el resto es
// opcional. Se guarda en localStorage -- es una preferencia de sesión larga, no algo
// que deba perderse al cambiar de pantalla o recargar.
const LLAVE_COLUMNAS_OCULTAS = 'ibu_contenedores_columnas_ocultas';
const COLUMNAS_OPCIONALES = [
  { key: 'etaReal', label: 'ETA' },
  { key: 'navieras', label: 'Naviera' },
  { key: 'clientes', label: 'Cliente' },
  { key: 'agentes', label: 'Agente Aduanal' },
  { key: 'forwarders', label: 'Forwarder' },
  { key: 'patentes', label: 'Patente' },
  { key: 'puerto', label: 'Puerto' },
  { key: 'modalidad', label: 'Modalidad' },
  { key: 'asegurado', label: 'Asegurado' },
  { key: 'fechaModulacion', label: 'Modulación' },
  { key: 'status', label: 'Status' },
  { key: 'fechaRevalidacion', label: 'Revalidación' },
  { key: 'fechaEntregaCliente', label: 'Entrega Cliente' },
];


// Confirmación de baja/alta: mismo Swal que el componente `Baja` de V1.
const NAVIERA_GENERICA = '65a093c4b196000019bbeca4'; // V1: SG deshabilitado para esta naviera.
const ITEMS_PER_PAGE = 50; // V1: ITEMS_PER_PAGE = 50.
const formatDate = (d) => {
  if (!d) return '';
  const p = String(d).split('-');
  return p.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : d;
};
const idDe = (arr) => (Array.isArray(arr) && arr[0] ? arr[0]._id : undefined);
const fmtUSD = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(n || 0);
const isoHoy = () => new Date().toISOString().slice(0, 10);
const isoHace365 = () => { const d = new Date(); d.setDate(d.getDate() - 365); return d.toISOString().slice(0, 10); };

// V1 arma estos campos en pantalla (`ListadoContenedores.jsx` líneas 273 y 303), no se guardan así.
const numeroInternoDe = (r) => `${nombreDe(r.clientes, 'nombre_comercial')}-${r.idContenedor}`;
const statusDe = (r) => (r.maniobras === 'Si' ? `${r.status} ${r.fechaManiobras}` : r.status);

const filtroSelectCls = 'select-base w-full text-xs py-1';

export default function ContenedoresListPage() {
  const { hasPermiso } = useContext(AuthContext);
  const navigate = useNavigate();
  const [mostrarEtaMasiva, setMostrarEtaMasiva] = useState(false);
  const [exportando, setExportando] = useState(null);
  const [filaArticulos, setFilaArticulos] = useState(null);
  const [filaStatus, setFilaStatus] = useState(null);
  const [filaMapa, setFilaMapa] = useState(null);
  const [mailOpen, setMailOpen] = useState(false);

  const [columnasOcultas, setColumnasOcultas] = useState(() => {
    try {
      const guardado = JSON.parse(localStorage.getItem(LLAVE_COLUMNAS_OCULTAS) || '[]');
      return new Set(Array.isArray(guardado) ? guardado : []);
    } catch {
      return new Set();
    }
  });
  const [menuColumnasAbierto, setMenuColumnasAbierto] = useState(false);
  const menuColumnasRef = useRef(null);

  useEffect(() => {
    function cerrarAlHacerClickFuera(e) {
      if (menuColumnasRef.current && !menuColumnasRef.current.contains(e.target)) setMenuColumnasAbierto(false);
    }
    document.addEventListener('mousedown', cerrarAlHacerClickFuera);
    return () => document.removeEventListener('mousedown', cerrarAlHacerClickFuera);
  }, []);

  function alternarColumna(key) {
    setColumnasOcultas((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      try {
        localStorage.setItem(LLAVE_COLUMNAS_OCULTAS, JSON.stringify([...next]));
      } catch {
        // localStorage puede fallar en modo privado -- no es crítico, se ignora.
      }
      return next;
    });
  }

  const puedeVer = hasPermiso('contenedores:ver');
  const puedeCrear = hasPermiso('contenedores:crear');
  const puedeEditar = hasPermiso('contenedores:editar');
  const puedeEntrega = hasPermiso('contenedores:entrega'); // V1: `user.contenedoresEntrega`

  // Catálogos de los selects de filtro por columna (V1: proveedores por `tipo` + clientes).
  const [clientes, setClientes] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [articulosCatalogo, setArticulosCatalogo] = useState([]);
  useEffect(() => {
    traerCatalogo('/clientes').then((r) => setClientes(ordenarCatalogo(r, 'nombre_comercial'))).catch(() => {});
    traerCatalogo('/proveedores').then(setProveedores).catch(() => {});
    traerCatalogo('/articulos').then((r) => setArticulosCatalogo(ordenarCatalogo(r))).catch(() => {});
  }, []);
  const proveedoresDe = (tipo) => ordenarCatalogo(proveedores.filter((p) => p.tipo === tipo));

  // Memoizado: ver bug real corregido en `ResourcePage.jsx` 2026-09-28.
  const listar = useCallback((params, opts) => apiClient.get('/contenedores', { params, signal: opts?.signal }).then((r) => r.data), []);
  // Filtros iniciales = estado inicial de V1: fechas (hoy-365 .. hoy), Pendientes = "No"
  // (`entregaCliente`), Activo = "Si". `status` es el param histórico de la API para
  // `is_active`; `estatus` filtra el campo `status` del contenedor.
  const filtrosIniciales = useMemo(() => ({
    fechaInicio: isoHace365(), fechaFin: isoHoy(), entregaCliente: 'No', status: 'Si',
  }), []);
  const {
    filas, paginacion, cargando, error,
    pagina, setPagina, sortBy, sortOrder, ordenarPor,
    filtros, cambiarFiltros, busqueda, setBusqueda, recargar,
  } = useListado({ listar, filtrosIniciales, ordenPor: 'etaReal', orden: 'asc', porPagina: ITEMS_PER_PAGE });

  async function cambiarActivo(row) {
    const baja = row.is_active !== 'No';
    const { isConfirmed } = await Swal.fire({
      title: 'Estas seguro?',
      text: baja ? 'Se dara de baja!' : 'Se dara de Alta!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: baja ? 'Si, dar de Baja!' : 'Si, dar de Alta!',
    });
    if (!isConfirmed) return;
    try {
      await apiClient.patch(`/contenedores/${row._id}`, { is_active: baja ? 'No' : 'Si' });
      Swal.fire({ position: 'center', icon: 'success', title: baja ? 'Se dio de Baja' : 'Se dio de Alta', showConfirmButton: false, timer: 2000 });
      await recargar();
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Oops...', text: 'Something went wrong!', footer: e.message });
    }
  }

  async function registrarSG(row) {
    try {
      await apiClient.patch(`/contenedores/${row._id}/rastreo`, {
        navieras: nombreDe(row.navieras),
        contenedor: row.contenedor,
      });
      Swal.fire({ icon: 'success', title: 'Good job!', text: 'Creado con exito', timer: 1500, showConfirmButton: false });
      await recargar();
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Oops...', text: 'Something went wrong!', footer: e.message });
    }
  }

  // PDF por fila (botón rojo con ícono PDF de V1: `PDFOC`) -- ficha del contenedor con
  // sus artículos: "Contenedor # n", Fecha, Naviera, Piezas, tabla Articulo/Descripcion/
  // Cantidad/Precio/Total y pie con Total.
  async function pdfContenedor(row) {
    try {
      const { data } = await apiClient.get(`/articulos-contenedor/contenedor/${row._id}`);
      const body = (data.data || []).map((a) => [
        a.articulos?.name || a.articulos?.[0]?.name || '',
        a.descripcion,
        new Intl.NumberFormat('en-US').format(a.cantidad),
        fmtUSD(a.precio),
        fmtUSD(a.total),
      ]);
      const doc = new jsPDF({ orientation: 'landscape' });
      await agregarLogosPdf(doc, { orientation: 'landscape' });
      doc.setFontSize(16);
      doc.text(`Contenedor # ${row.idContenedor}`, 20, 20);
      doc.setFontSize(12);
      doc.text(`Fecha ${row.fecha}`, 20, 25);
      doc.text(`Naviera ${nombreDe(row.navieras)}`, 20, 30);
      doc.text(`Piezas ${new Intl.NumberFormat('en-US').format(row.totalCantidad || 0)}`, 20, 40);
      autoTable(doc, {
        head: [['Articulo', 'Descripcion', 'Cantidad', 'Precio', 'Total']],
        body,
        startY: 45,
        foot: [['', '', '', 'Total', fmtUSD(row.totalGeneral)]],
        showFoot: 'lastPage',
      });
      doc.save(`Contenedor-${row.idContenedor}.pdf`);
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'No se pudo generar el PDF', text: e.message });
    }
  }

  // ---- PDF / Excel del listado (columnas EXACTAS de `PDFTabla()`/`excel()` de V1; son
  // distintas a las de la tabla en pantalla).
  const columnasPdf = [
    { key: 'etaReal', label: 'ETA' },
    { key: 'numeroInterno', label: 'No. Interno', render: numeroInternoDe },
    { key: 'contenedor', label: 'Contenedor' },
    { key: 'navieras', label: 'Naviera', render: (r) => nombreDe(r.navieras) },
    { key: 'clientes', label: 'Cliente', render: (r) => nombreDe(r.clientes, 'nombre_comercial') },
    { key: 'forwarders', label: 'Forwarder', render: (r) => nombreDe(r.forwarders) },
    { key: 'puerto', label: 'Puerto' },
    { key: 'modalidad', label: 'Modalidad' },
    { key: 'fechaModulacion', label: 'Modulacion' },
    { key: 'fechaEntregaCliente', label: 'Entrega Cliente' },
    { key: 'status', label: 'Status', render: statusDe },
    { key: 'observaciones', label: 'Observaciones' },
  ];
  const columnasExcel = [
    { key: 'ID', label: 'ID', render: (r) => r._id },
    { key: 'Numero', label: 'Numero', render: (r) => r.idContenedor },
    { key: 'ETA', label: 'ETA', render: (r) => r.etaReal },
    { key: 'NumeroInterno', label: 'NumeroInterno', render: numeroInternoDe },
    { key: 'Contenedor', label: 'Contenedor', render: (r) => r.contenedor },
    { key: 'Naviera', label: 'Naviera', render: (r) => nombreDe(r.navieras) },
    { key: 'Cliente', label: 'Cliente', render: (r) => nombreDe(r.clientes, 'nombre_comercial') },
    { key: 'AgenteAduanal', label: 'AgenteAduanal', render: (r) => nombreDe(r.agentes) },
    { key: 'Forwarder', label: 'Forwarder', render: (r) => nombreDe(r.forwarders) },
    { key: 'Puerto', label: 'Puerto', render: (r) => r.puerto },
    { key: 'Modalidad', label: 'Modalidad', render: (r) => r.modalidad },
    { key: 'Asegurado', label: 'Asegurado', render: (r) => r.asegurado },
    { key: 'FechaPagoPedimento', label: 'FechaPagoPedimento', render: (r) => r.fechaPagoPedimento },
    { key: 'Modulacion', label: 'Modulacion', render: (r) => r.fechaModulacion },
    { key: 'Status', label: 'Status', render: statusDe },
    { key: 'Maniobras', label: 'Maniobras', render: (r) => r.fechaManiobras },
    { key: 'Patente', label: 'Patente', render: (r) => nombreDe(r.patentes) },
    { key: 'EntregaCliente', label: 'EntregaCliente', render: (r) => r.fechaEntregaCliente },
    { key: 'Observaciones', label: 'Observaciones', render: (r) => r.observaciones },
    { key: 'ObservacionesInternas', label: 'ObservacionesInternas', render: (r) => r.observacionesInternas },
    { key: 'Articulo', label: 'Articulo', render: (r) => nombreDe(r.articulos) },
    { key: 'FechaRevalidacion', label: 'FechaRevalidacion', render: (r) => r.fechaRevalidacion },
  ];

  // Trae TODO el listado filtrado (V1 exportaba todo el filtro, sin paginar). El backend
  // topa `limit` en 100, así que se recorren las páginas.
  async function traerTodoFiltrado() {
    const todas = [];
    let page = 1;
    let totalPages = 1;
    do {
      const { data } = await apiClient.get('/contenedores', {
        params: { ...filtros, search: busqueda || undefined, sortBy, sortOrder, page, limit: 100 },
      });
      todas.push(...(data?.data ?? []));
      totalPages = data?.meta?.pagination?.totalPages || 1;
      page += 1;
    } while (page <= totalPages && page <= 200);
    return todas;
  }

  // Columnas del PDF que V1 manda por eMail (`enviaMail()`): son 20, distintas a las del botón PDF.
  const columnasPdfMail = [
    { key: 'idContenedor', label: 'Numero' },
    { key: 'fecha', label: 'Fecha' },
    { key: 'etaReal', label: 'ETA' },
    { key: 'numeroInterno', label: 'No. Interno', render: numeroInternoDe },
    { key: 'contenedor', label: 'Contenedor' },
    { key: 'navieras', label: 'Naviera', render: (r) => nombreDe(r.navieras) },
    { key: 'clientes', label: 'Cliente', render: (r) => nombreDe(r.clientes, 'nombre_comercial') },
    { key: 'agentes', label: 'Agente Aduanal', render: (r) => nombreDe(r.agentes) },
    { key: 'forwarders', label: 'Forwarder', render: (r) => nombreDe(r.forwarders) },
    { key: 'puerto', label: 'Puerto' },
    { key: 'terminal', label: 'Terminal', render: (r) => nombreDe(r.terminales) },
    { key: 'modalidad', label: 'Modalidad' },
    { key: 'fechaSalidaPuerto', label: 'Salida Puerto' },
    { key: 'fechaSalidaRuta', label: 'Salida Ruta' },
    { key: 'status', label: 'Status', render: statusDe },
    { key: 'observaciones', label: 'Observaciones' },
    { key: 'programa', label: 'Programa', render: (r) => nombreDe(r.programas) },
    { key: 'totalCantidad', label: 'Cantidad' },
    { key: 'totalGeneral', label: 'Total', render: (r) => fmtUSD(r.totalGeneral) },
    { key: 'direccionEntrega', label: 'Direccion Entrega' },
  ];

  // eMail (V1 `enviaMail`): destinatario inicial vacío como V1 (`mailTo`).
  async function enviarMail(email) {
    const doc = await buildTablePdf('Listado Contenedores', columnasPdfMail, await traerTodoFiltrado(), {
      subtitulo: `${filtros.fechaInicio || ''} - ${filtros.fechaFin || ''}`,
    });
    await enviarCorreoAdjunto({
      subject: 'Listado Contenedores', email, fileName: 'ListadoContenedores.pdf', docPdf: doc,
      textoHtml: plantillaCorreoV1('Adjunto encontraras el listado de Contenedores.'),
    });
  }

  async function exportarPDF() {
    setExportando('pdf');
    try {
      await exportTablePdf('Listado Contenedores', columnasPdf, await traerTodoFiltrado(), {
        subtitulo: `${filtros.fechaInicio || ''} - ${filtros.fechaFin || ''}`, fileName: 'Contenedores',
      });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'No se pudo exportar', text: e.message });
    } finally {
      setExportando(null);
    }
  }

  async function exportarExcel() {
    setExportando('excel');
    try {
      exportTableExcel('ListadoContenedores', columnasExcel, await traerTodoFiltrado(), {
        fileName: 'ListadoContenedores', sheetName: 'ListadoContenedores',
      });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'No se pudo exportar', text: e.message });
    } finally {
      setExportando(null);
    }
  }

  if (!puedeVer) return <div className="text-slate-500">No tienes permiso para ver este módulo.</div>;

  const setFiltro = (clave) => (e) => cambiarFiltros({ [clave]: e.target.value || undefined });
  const selectFiltro = (clave, opciones) => (
    <select className={filtroSelectCls} value={filtros[clave] || ''} onChange={setFiltro(clave)}>
      <option value="">Selecciona</option>
      {opciones}
    </select>
  );
  const opcionesProv = (tipo) => proveedoresDe(tipo).map((p) => <option key={p._id} value={p._id}>{p.name}</option>);

  // Columnas = `<tbody>` real de V1 (No. Interno/Contenedor/ETA/Naviera/Cliente/Agente
  // Aduanal/Forwarder/Patente/Puerto/Modalidad/Asegurado/Modulación/Status/
  // Revalidación/Entrega Cliente/Acciones), cada una con su filtro de la primera fila
  // de V1 (Naviera, Cliente, Agente, Forwarder, Puerto, Modalidad, Asegurado, Status).
  // Ordenar por nombre de naviera/cliente/agente/forwarder/patente no es posible en el
  // servidor (son refs), por eso esas columnas no son ordenables.
  const columnas = [
    { key: 'numeroInterno', label: 'No. Interno', sortable: true, render: numeroInternoDe },
    { key: 'contenedor', label: 'Contenedor', sortable: true },
    { key: 'etaReal', label: 'ETA', sortable: true },
    { key: 'navieras', label: 'Naviera', sortable: true, wrap: true, render: (r) => nombreDe(r.navieras), filter: selectFiltro('navieras', opcionesProv('Naviera')) },
    {
      key: 'clientes', label: 'Cliente', sortable: true, wrap: true, render: (r) => nombreDe(r.clientes, 'nombre_comercial'),
      filter: selectFiltro('clientes', clientes.map((c) => <option key={c._id} value={c._id}>{c.nombre_comercial}</option>)),
    },
    { key: 'agentes', label: 'Agente Aduanal', sortable: true, wrap: true, render: (r) => nombreDe(r.agentes), filter: selectFiltro('agentes', opcionesProv('Agente')) },
    { key: 'forwarders', label: 'Forwarder', sortable: true, wrap: true, render: (r) => nombreDe(r.forwarders), filter: selectFiltro('forwarders', opcionesProv('Forwarder')) },
    { key: 'patentes', label: 'Patente', sortable: true, wrap: true, render: (r) => nombreDe(r.patentes) },
    {
      key: 'puerto', label: 'Puerto', sortable: true,
      filter: selectFiltro('puerto', PUERTOS.map((p) => <option key={p} value={p}>{p}</option>)),
    },
    {
      key: 'modalidad', label: 'Modalidad', sortable: true,
      filter: selectFiltro('modalidad', MODALIDADES.map((m) => <option key={m} value={m}>{m}</option>)),
    },
    {
      key: 'asegurado', label: 'Asegurado', sortable: true,
      filter: selectFiltro('asegurado', ASEGURADO_OPCIONES.map((a) => <option key={a} value={a}>{a}</option>)),
    },
    { key: 'fechaModulacion', label: 'Modulación', sortable: true, render: (r) => formatDate(r.fechaModulacion) },
    {
      key: 'status', label: 'Status', render: statusDe,
      filter: selectFiltro('estatus', [...STATUS_OPCIONES, 'Entregado', 'Cancelado'].map((s) => <option key={s} value={s}>{s}</option>)),
    },
    { key: 'fechaRevalidacion', label: 'Revalidación', sortable: true, render: (r) => formatDate(r.fechaRevalidacion) },
    { key: 'fechaEntregaCliente', label: 'Entrega Cliente', sortable: true },
    {
      key: '__acciones', label: 'Acciones', sortable: false, className: 'text-center',
      render: (row) => {
        const sgActivo = row.rastreo === 'No' && idDe(row.navieras) !== NAVIERA_GENERICA;
        const mapaActivo = row.mapa !== 'No';
        const btn = 'p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent';
        return (
          <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
            <button title="PDF" aria-label="PDF" className={`${btn} text-danger-500`} onClick={() => pdfContenedor(row)}>
              <FileText size={15} />
            </button>
            {puedeEditar && (
              <button
                title="SG (registrar en ShipsGo)" aria-label="SG" disabled={!sgActivo}
                className={`${btn} text-success-600 text-xs font-semibold`} onClick={() => registrarSG(row)}
              >
                SG
              </button>
            )}
            <button title="Mapa" aria-label="Mapa" disabled={!mapaActivo} className={`${btn} text-slate-600`} onClick={() => setFilaMapa(row)}>
              <MapIcon size={15} />
            </button>
            {puedeEditar && (
              <button title="Editar" aria-label="Editar" className={`${btn} text-primary-600`} onClick={() => navigate(`/contenedores/${row._id}`)}>
                <Pencil size={15} />
              </button>
            )}
            {puedeEditar && (
              <button title="Editar Articulos" aria-label="Editar Articulos" className={`${btn} text-primary-600`} onClick={() => setFilaArticulos(row)}>
                <ShoppingCart size={15} />
              </button>
            )}
            {puedeEditar && (
              <button title="Status" aria-label="Status" className={`${btn} text-success-600`} onClick={() => setFilaStatus(row)}>
                <CalendarCheck size={15} />
              </button>
            )}
            {puedeEntrega && (
              <button
                title={row.is_active === 'No' ? 'Dar de Alta' : 'Dar de baja'}
                aria-label={row.is_active === 'No' ? 'Dar de Alta' : 'Dar de baja'}
                className={`${btn} ${row.is_active === 'No' ? 'text-success-600' : 'text-danger-500'}`}
                onClick={() => cambiarActivo(row)}
              >
                {row.is_active === 'No' ? <RotateCcw size={15} /> : <Trash2 size={15} />}
              </button>
            )}
          </div>
        );
      },
    },
  ];

  const columnasVisibles = columnas.filter((c) => c.key === '__acciones' || !columnasOcultas.has(c.key));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/contenedores/nuevo')} disabled={!puedeCrear}
            className="btn-primary disabled:opacity-50"
          >
            Nuevo
          </button>
          <button onClick={() => navigate('/')} className="btn-outline">Regresar</button>
          {puedeEditar && (
            <button onClick={() => setMostrarEtaMasiva(true)} className="btn-outline">
              Carga masiva ETA
            </button>
          )}
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={exportarPDF} disabled={!!exportando} className="btn-outline text-xs px-3 py-1.5">
            {exportando === 'pdf' ? 'Generando...' : 'PDF'}
          </button>
          <button type="button" onClick={() => setMailOpen(true)} disabled={!!exportando} className="btn-outline text-xs px-3 py-1.5">eMail</button>
          <button type="button" onClick={exportarExcel} disabled={!!exportando} className="btn-outline text-xs px-3 py-1.5">
            {exportando === 'excel' ? 'Generando...' : 'Excel'}
          </button>
        </div>
      </div>

      <h1 className="text-xl font-semibold text-slate-900 text-center">Contenedores</h1>

      <div className="flex flex-wrap items-center gap-3 justify-between">
        <input
          value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Buscar..."
          className="input-base w-64"
        />
        {/* Mostrar/ocultar columnas -- pedido explícito del usuario (2026-09-29). */}
        <div className="relative" ref={menuColumnasRef}>
          <button
            type="button" onClick={() => setMenuColumnasAbierto((v) => !v)}
            className="btn-outline text-xs px-3 py-1.5 inline-flex items-center gap-1.5"
          >
            <Columns3 size={14} /> Columnas
          </button>
          {menuColumnasAbierto && (
            <div className="absolute right-0 mt-2 w-56 card p-2 z-20 max-h-80 overflow-y-auto">
              {COLUMNAS_OPCIONALES.map((c) => (
                <label key={c.key} className="flex items-center gap-2 px-2 py-1.5 text-sm rounded-md hover:bg-slate-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!columnasOcultas.has(c.key)}
                    onChange={() => alternarColumna(c.key)}
                  />
                  <span className="min-w-0">{c.label}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className="label">Fecha Inicio</label>
          <input type="date" className="input-base" value={filtros.fechaInicio || ''} onChange={setFiltro('fechaInicio')} />
        </div>
        <div>
          <label className="label">Fecha Fin</label>
          <input type="date" className="input-base" value={filtros.fechaFin || ''} onChange={setFiltro('fechaFin')} />
        </div>
        <div>
          <label className="label">Pendientes</label>
          <select className="select-base" value={filtros.entregaCliente || ''} onChange={setFiltro('entregaCliente')}>
            <option value="">Selecciona</option>
            <option value="No">Pendiente</option>
            <option value="Si">Entregado</option>
          </select>
        </div>
        <div>
          <label className="label">Activo</label>
          <select
            className="select-base" value={filtros.status === 'all' ? '' : (filtros.status || '')}
            onChange={(e) => cambiarFiltros({ status: e.target.value || 'all' })}
          >
            <option value="">Selecciona</option>
            <option value="Si">Activo</option>
            <option value="No">Cancelado</option>
          </select>
        </div>
        {error && <span className="text-sm text-danger-600">{error}</span>}
      </div>

      <DataTable
        columns={columnasVisibles} data={filas} loading={cargando}
        sortBy={sortBy} sortOrder={sortOrder} onSort={ordenarPor}
      />
      <Pagination pagination={paginacion} page={pagina} onChange={setPagina} />

      <EmailModal open={mailOpen} onClose={() => setMailOpen(false)} titulo="Enviar Listado Contenedores" onEnviar={enviarMail} />
      {mostrarEtaMasiva && (
        <EtaMasivaModal
          onClose={() => setMostrarEtaMasiva(false)}
          onTerminado={recargar}
        />
      )}
      {filaArticulos && (
        <ArticulosContenedorModal
          contenedor={filaArticulos} articulosCatalogo={articulosCatalogo}
          onClose={() => setFilaArticulos(null)} onGuardado={recargar}
        />
      )}
      {filaStatus && (
        <StatusContenedorModal
          contenedor={filaStatus} numeroInterno={numeroInternoDe(filaStatus)}
          onClose={() => setFilaStatus(null)} onGuardado={recargar}
        />
      )}
      {filaMapa && <MapaTrackingModal contenedor={filaMapa} onClose={() => setFilaMapa(null)} />}
    </div>
  );
}
